import React from 'react';
import { Copy, ExternalLink, Users } from 'lucide-react';

const DEFAULT_COPY =
  'A booking for this itinerary and these passengers already exists in our system. You can open it below — we do not create a second booking for the same journey.';

/**
 * Shown when the airline API reports a duplicate booking (same pax + itinerary).
 * Copy is supplier-neutral for guests and agents.
 */
export default function DuplicateBookingPanel({
  title = 'Booking already exists',
  message,
  acfBookingId,
  isAgent = false,
  onViewBooking,
  onGoBookings,
  onGoHome,
}) {
  const copyId = async () => {
    if (!acfBookingId) return;
    try {
      await navigator.clipboard.writeText(String(acfBookingId));
    } catch {
      /* ignore */
    }
  };

  const bodyText = (message && String(message).trim()) || DEFAULT_COPY;

  return (
    <div className="max-w-lg w-full mx-auto rounded-2xl border border-amber-200 bg-amber-50/90 p-6 shadow-lg text-left">
      <div className="flex items-start gap-3">
        <div className="shrink-0 rounded-full bg-amber-100 p-2">
          <Users className="w-6 h-6 text-amber-800" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <p className="mt-2 text-sm text-gray-700 whitespace-pre-line">{bodyText}</p>
        </div>
      </div>

      <div className="mt-5 space-y-3 rounded-xl bg-white p-4 border border-amber-100">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Your ACF booking ID</p>
        {acfBookingId ? (
          <div className="flex flex-wrap items-center gap-2">
            <code className="text-base font-mono font-bold text-[#1B375B] break-all" data-testid="acf-booking-id">
              {acfBookingId}
            </code>
            <button
              type="button"
              onClick={copyId}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              <Copy className="w-3.5 h-3.5" />
              Copy
            </button>
          </div>
        ) : (
          <p className="text-sm text-gray-600">
            We could not link this automatically to a single record. Open{' '}
            <strong>{isAgent ? 'My Bookings' : 'My bookings'}</strong> and find the flight by date and passenger
            name, or contact support with your payment or order reference.
          </p>
        )}
      </div>

      <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {acfBookingId && onViewBooking ? (
          <button
            type="button"
            onClick={onViewBooking}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#1B375B] px-4 py-3 text-sm font-semibold text-white hover:bg-[#335E77]"
          >
            <ExternalLink className="w-4 h-4" />
            Open booking
          </button>
        ) : null}
        {onGoBookings ? (
          <button
            type="button"
            onClick={onGoBookings}
            className="inline-flex flex-1 items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-50"
          >
            {isAgent ? 'All bookings' : 'My bookings'}
          </button>
        ) : null}
        {onGoHome ? (
          <button
            type="button"
            onClick={onGoHome}
            className="inline-flex flex-1 items-center justify-center rounded-xl border border-transparent px-4 py-3 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            Home
          </button>
        ) : null}
      </div>
    </div>
  );
}
